import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ICityWithTowns } from "../models/ICityList";
import { getCityListPublic } from "../services/city";
import { getTokenForWeb } from "../services/auth";
import { isLoggedIn } from "../utils/tokenUtils";
import { useLanguage } from "../context/LanguageContext";
import GoogleLoginButton from "../components/GoogleLoginButton";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

interface Props {
  onClose: () => void;
}

function CitySelectPopup({ onClose }: Props) {
  useBodyScrollLock();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [cities, setCities] = useState<ICityWithTowns[]>([]);
  const [cityId, setCityId] = useState("");
  const [townId, setTownId] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getTokenForWeb()
      .then(() => getCityListPublic())
      .then((res) => {
        const sorted = res.data
          .sort((a, b) => a.cityNameTr.localeCompare(b.cityNameTr, "tr"))
          .map((c) => ({
            ...c,
            town: [...c.town].sort((a, b) => a.townNameTr.localeCompare(b.townNameTr, "tr")),
          }));
        setCities(sorted);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const selectedCity = cities.find((c) => c.id === cityId);

  const handleContinue = () => {
    if (!townId || !selectedCity) return;
    const town = selectedCity.town.find((tw) => tw.id === townId);
    localStorage.setItem("guestTownId", townId);
    localStorage.setItem("guestLocationName", `${selectedCity.cityNameTr} / ${town?.townNameTr ?? ""}`);
    onClose();
    navigate("/restaurants");
  };

  const handleLoginSuccess = () => {
    onClose();
    window.location.reload();
  };

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{ background: "rgba(0,0,0,0.5)", zIndex: 1060 }}
    >
      <div className="bg-white rounded-4 shadow p-4 position-relative" style={{ width: "420px", maxWidth: "90vw" }}>
        <button
          type="button"
          className="btn-close position-absolute"
          style={{ top: "16px", right: "16px" }}
          aria-label={t("common.close")}
          onClick={onClose}
        />
        <div className="text-center mb-3">
          <i className="bi bi-geo-alt-fill" style={{ fontSize: "2.5rem", color: "#c20e1a" }}></i>
          <h5 className="mt-2 fw-bold">{t("citySelect.title")}</h5>
          <p className="text-muted small mb-0">{t("citySelect.subtitle")}</p>
        </div>

        {loading ? (
          // Dikeyde ortalanmis bir kutu oldugu icin spinner -> icerik gecisinde
          // yukseklik degisirse popup yeniden ortalanip ziplyor. Yuklenen halin
          // yaklasik yuksekligini simdiden ayiriyoruz.
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ minHeight: "132px" }}
          >
            <span className="spinner-border text-danger"></span>
          </div>
        ) : (
          <>
            <div className="mb-3">
              <label className="form-label fw-semibold small">{t("citySelect.city")}</label>
              <select
                className="form-select"
                value={cityId}
                onChange={(e) => { setCityId(e.target.value); setTownId(""); }}
              >
                <option value="">{t("citySelect.selectCity")}</option>
                {cities.map((c) => (
                  <option key={c.id} value={c.id}>{c.cityNameTr}</option>
                ))}
              </select>
            </div>

            {selectedCity && (
              <div className="mb-3">
                <label className="form-label fw-semibold small">{t("citySelect.town")}</label>
                <select
                  className="form-select"
                  value={townId}
                  onChange={(e) => setTownId(e.target.value)}
                >
                  <option value="">{t("citySelect.selectTown")}</option>
                  {selectedCity.town.map((tw) => (
                    <option key={tw.id} value={tw.id}>{tw.townNameTr}</option>
                  ))}
                </select>
              </div>
            )}

            <button
              className="btn btn-danger w-100 rounded-pill py-2 fw-semibold"
              disabled={!townId}
              onClick={handleContinue}
            >
              <i className="bi bi-arrow-right me-2"></i>
              {t("citySelect.continue")}
            </button>
          </>
        )}

        <div className="d-flex align-items-center my-3">
          <hr className="flex-grow-1" />
          <span className="px-2 text-muted small">{t("citySelect.or")}</span>
          <hr className="flex-grow-1" />
        </div>

        <button
          className="btn btn-outline-secondary w-100 rounded-pill py-2 fw-semibold mb-2"
          data-bs-toggle="modal"
          data-bs-target="#loginModal"
          onClick={onClose}
        >
          <i className="bi bi-box-arrow-in-right me-2"></i>
          {t("login.title")}
        </button>

        <GoogleLoginButton onLoginSuccess={handleLoginSuccess} />
      </div>
    </div>
  );
}

export default CitySelectPopup;
